// The pure cell-by-cell hive-composition model (hive-egg-cells; client-rendering "Cell-by-cell
// hive drawables"). Mirrors the `dance.ts`/`corpse.ts` seam: pure and Pixi-free — the renderer
// feeds it the confirmed close-tier `WorldSnapshot` cells (already dequantized to world units)
// and reads world-space drawables to project into the y-sorted draw list. The GPU paint itself
// stays device-exercised only (repo convention D-086).
//
// The hive renders cell-by-cell so the union of stamped cells IS the colony's build state
// (UI.md §8). Each cell picks one fill by priority — ruin (decaying wax) > damaged > intact —
// and an OCCUPIED cell additionally carries an **additive egg marker** drawn on top of that
// fill, so "which cells have eggs" is legible at a glance regardless of the cell's health (the
// acceptance bar). Cells render only at the close tier; they are static geometry, so the `full`
// and `reduced` animation modes render them identically, and the wide tier (`none`) renders no
// per-cell hive. A connection/tier discontinuity resets the snapshot's cell set upstream (the
// wire layer's `emptyWorld`), so this pure seam naturally yields no cells after a reset.

/** A close-tier hive cell the renderer feeds in: id, dequantized world position, and the three
 * decoded orthogonal flags. */
export interface HiveCellInput {
  readonly id: number;
  readonly position: readonly [number, number];
  readonly occupied: boolean;
  readonly damaged: boolean;
  readonly ruin: boolean;
}

/** The confirmed close-tier world the composer reads (its cell set). */
export interface HiveCellWorld {
  readonly cells: ReadonlyMap<number, HiveCellInput>;
}

/** The render modes over the two wire tiers (UI.md §8): `full`/`reduced` (close) render cells
 * identically (static geometry); `none` (wide tier) renders no per-cell hive. */
export type HiveCellRenderMode = 'full' | 'reduced' | 'none';

/** The chosen cell fill, by priority: `ruin` (decaying wax) > `damaged` > `intact`. */
export type HiveCellFill = 'intact' | 'damaged' | 'ruin';

/** One stamped hex cell at its world position, with its chosen fill. The union of these forms
 * the hive silhouette. */
export interface HiveCellDrawable {
  readonly kind: 'hive-cell';
  readonly id: number;
  readonly position: readonly [number, number];
  readonly fill: HiveCellFill;
  /** This cell's current damage-flash intensity (0..1) from `DamageFlashTracker.flashes`, or
   * absent when no flash is active (combat-strikes-and-damage-flash, design E7/E8). Its own
   * field, exactly like `WorldDrawable.flashIntensity` — the flash composes OVER the chosen
   * `fill` rather than being a fourth fill, so a struck ruin still reads as a ruin. */
  readonly flashIntensity?: number;
}

/** The additive egg/brood marker drawn on top of an OCCUPIED cell's fill (never a substitute
 * fill), so an egg-occupied cell is unmistakably distinct from an empty one at any health. */
export interface HiveEggMarkerDrawable {
  readonly kind: 'hive-egg';
  readonly id: number;
  readonly position: readonly [number, number];
}

export type HiveCellDrawableItem = HiveCellDrawable | HiveEggMarkerDrawable;

/** The fill a cell selects, by priority (ruin > damaged > intact). */
export function cellFill(cell: HiveCellInput): HiveCellFill {
  if (cell.ruin) return 'ruin';
  if (cell.damaged) return 'damaged';
  return 'intact';
}

/** One frame of `/sprites/hive_cells.png`, square at this pitch (not the 24px directional
 * pitch — the comb tile carries a tower crane that needs headroom above the hex). */
export const HIVE_CELL_FRAME_SIZE = 40;

/** Column order in the sheet, as `tools/blender/build_hive_cell.py` lays it out. Keep the
 * two in step: the generator's `STATES` tuple is the other half of this contract. */
export const HIVE_CELL_FRAMES = Object.freeze({
  building: 0,
  intact: 1,
  intactEgg: 2,
  damaged: 3,
  damagedEgg: 4,
  ruin: 5,
});

/**
 * The sheet column for one cell: its `fill`, whether it is brooding (the egg is baked into
 * the tile rather than composited over it, so it sits *in* the bore under the same light),
 * and whether it is still presenting as freshly built.
 *
 * `building` wins over everything, but only ever reaches an `intact` cell — {@link
 * FreshCellTracker} marks a cell fresh at first sight, and a cell that arrives already
 * damaged or ruined is not something that was just built. A fresh cell hides its egg for
 * the moment it is under construction; the brood marker returns when the scaffold comes
 * down, which is also the truth of it (a cell is built before it is laid in).
 */
export function hiveCellFrame(fill: HiveCellFill, occupied: boolean, fresh = false): number {
  if (fill === 'intact') {
    if (fresh) return HIVE_CELL_FRAMES.building;
    return occupied ? HIVE_CELL_FRAMES.intactEgg : HIVE_CELL_FRAMES.intact;
  }
  if (fill === 'damaged') {
    return occupied ? HIVE_CELL_FRAMES.damagedEgg : HIVE_CELL_FRAMES.damaged;
  }
  return HIVE_CELL_FRAMES.ruin;
}

/** How long a newly-built cell presents as under construction. Purely presentational — the
 * wire has no build-progress flag (a cell springs into existence complete), so this is the
 * one honest thing the client knows: that the cell was not there a moment ago. */
export const CELL_BUILD_PRESENTATION_MS = 2500;

/**
 * Marks cells that have just appeared, so `build` reads as an event rather than nothing.
 *
 * The one trap this exists to avoid: a cell set does not only grow one cell at a time. A
 * trace load, a keyframe after a reconnect, or a wide→close tier switch delivers the whole
 * comb at once, and treating that as "every cell was just built" would put a crane on all
 * of them. So a set observed after an EMPTY one primes silently — the renderer resets the
 * comb through the same emptying (`emptyWorld`), which makes that the exact signal.
 */
export class FreshCellTracker {
  private readonly firstSeenMs = new Map<number, number>();
  private lastCount = 0;

  /** Feed one frame's cell ids and the frame's wall clock. */
  observe(ids: Iterable<number>, nowMs: number): void {
    const live = new Set(ids);
    const priming = this.lastCount === 0;
    for (const id of live) {
      if (!this.firstSeenMs.has(id)) {
        this.firstSeenMs.set(id, priming ? -Infinity : nowMs);
      }
    }
    for (const id of this.firstSeenMs.keys()) {
      if (!live.has(id)) this.firstSeenMs.delete(id);
    }
    this.lastCount = live.size;
  }

  /** True while `id` is still within its under-construction window. */
  fresh(id: number, nowMs: number): boolean {
    const seen = this.firstSeenMs.get(id);
    return seen !== undefined && nowMs - seen < CELL_BUILD_PRESENTATION_MS;
  }
}

/**
 * Builds the hive-cell drawables for one frame from the confirmed world and the render mode.
 * Pure: one `hive-cell` fill per cell (ruin > damaged > intact priority), plus an additive
 * `hive-egg` marker for each OCCUPIED cell (drawn after its fill so it sits on top). `full` and
 * `reduced` produce identical output (cells are static geometry — no per-RAF animation); `none`
 * (wide tier) produces nothing. Deterministic order: ascending cell id, and each cell's egg
 * marker immediately follows its fill.
 *
 * `flashFor` is the tracker's cell-flash lookup (`DamageFlashTracker.flashes`, `kind: 'cell'`) —
 * the same optional "derive once per RAF, read per drawable" seam `buildDrawables` takes for
 * bees. Omitted, no cell carries a flash: never a crash, only no treatment.
 */
export function hiveCellDrawables(
  world: HiveCellWorld,
  mode: HiveCellRenderMode,
  flashFor?: (id: number) => number | undefined,
): HiveCellDrawableItem[] {
  if (mode === 'none') return [];
  const out: HiveCellDrawableItem[] = [];
  const cells = [...world.cells.values()].sort((a, b) => a.id - b.id);
  for (const cell of cells) {
    const flashIntensity = flashFor?.(cell.id);
    out.push({
      kind: 'hive-cell',
      id: cell.id,
      position: cell.position,
      fill: cellFill(cell),
      ...(flashIntensity === undefined ? {} : { flashIntensity }),
    });
    if (cell.occupied) {
      out.push({ kind: 'hive-egg', id: cell.id, position: cell.position });
    }
  }
  return out;
}
